<template>
  <div class="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
    <!-- Sidebar -->
    <HrSidebar :isOpen="isSidebarOpen" />

    <!-- Main Content -->
    <main 
      class="flex-1 overflow-x-hidden transition-all duration-300"
      :class="{'ml-64': isSidebarOpen, 'ml-0': !isSidebarOpen}"
    >
      <!-- Navbar -->
      <header 
        class="fixed top-0 z-50 w-full bg-white dark:bg-gray-800 shadow-sm transition-all duration-300"
      >
        <HrTopbar 
          :page-title="currentPageTitle"
          :is-sidebar-open="isSidebarOpen"
          :is-dark-mode="isDarkMode"
          @toggle-sidebar="toggleSidebar"
          @toggle-dark-mode="toggleDarkMode"
        />
      </header>

      <div class="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-8 mt-20">
        <!-- Loading State -->
        <div v-if="loading" class="flex justify-center items-center py-20">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-[#035a76]"></div>
        </div>

        <!-- Error State -->
        <div
          v-else-if="error"
          class="bg-red-50 border-l-4 border-red-500 text-red-700 p-6 rounded-lg"
        >
          <p class="font-medium">Failed to load employee profile</p>
          <p class="mt-1">{{ error }}</p>
        </div>

        <!-- Content -->
        <template v-else-if="employee && employee.personal">
          <!-- Header -->
          <div class="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4">
            <div>
              <h1 class="text-2xl sm:text-3xl font-bold text-[#035a76] dark:text-[#4db3ff]">
                {{ employee.personal.first_name }} {{ employee.personal.last_name }}
              </h1>
              <p class="text-gray-600 dark:text-gray-400 mt-1">
                Employee ID: {{ employee.personal.employee_number || '—' }}
              </p>
            </div>

            <div class="flex items-center gap-4">
              <span
                :class="[
                  'px-4 py-1.5 rounded-full text-sm font-semibold',
                  getStatusClass(employee.contract?.status)
                ]"
              >
                {{ employee.contract?.status?.toUpperCase() || 'NO CONTRACT' }}
              </span>
              <button
                @click="router.push({ name: 'EditEmployeeProfile', params: { id: applicantId } })"
                class="px-4 py-2 bg-secondary text-white text-sm font-medium rounded-lg hover:bg-[#024c63] transition"
              >
                Update Profile
              </button>
            </div>
          </div>

          <!-- Two-column grid for main sections -->
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 xl:gap-8">
            <!-- Personal Information -->
            <section class="bg-white dark:bg-gray-800 shadow-sm rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <h2 class="text-xl font-semibold text-[#035a76] dark:text-[#4db3ff] mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
                Personal Information
              </h2>
              <dl class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
                <div>
                  <dt class="text-gray-500 dark:text-gray-400 font-medium">Employee Number</dt>
                  <dd class="mt-1 text-gray-900 dark:text-gray-100 font-medium">
                    {{ employee.personal.employee_number || '—' }}
                  </dd>
                </div>
                <div>
                  <dt class="text-gray-500 dark:text-gray-400 font-medium">Full Name</dt>
                  <dd class="mt-1 text-gray-900 dark:text-gray-100">
                    {{ [employee.personal.first_name, employee.personal.middle_name, employee.personal.last_name]
                      .filter(Boolean).join(' ') || '—' }}
                  </dd>
                </div>
                <div>
                  <dt class="text-gray-500 dark:text-gray-400 font-medium">Gender</dt>
                  <dd class="mt-1 capitalize">{{ employee.personal.gender || '—' }}</dd>
                </div>
                <div>
                  <dt class="text-gray-500 dark:text-gray-400 font-medium">Date of Birth</dt>
                  <dd class="mt-1">{{ formatDate(employee.personal.date_of_birth) }}</dd>
                </div>
                <div>
                  <dt class="text-gray-500 dark:text-gray-400 font-medium">Place of Birth</dt>
                  <dd class="mt-1">{{ employee.personal.place_of_birth || '—' }}</dd>
                </div>
                <div>
                  <dt class="text-gray-500 dark:text-gray-400 font-medium">Nationality</dt>
                  <dd class="mt-1">{{ employee.personal.nationality || '—' }}</dd>
                </div>
                <div>
                  <dt class="text-gray-500 dark:text-gray-400 font-medium">Marital Status</dt>
                  <dd class="mt-1 capitalize">{{ employee.personal.marital_status || '—' }}</dd>
                </div>
                <div>
                  <dt class="text-gray-500 dark:text-gray-400 font-medium">Date of Joining</dt>
                  <dd class="mt-1">{{ formatDate(employee.personal.date_of_join) }}</dd>
                </div>
              </dl>
            </section>

            <!-- Contact & Emergency Contact -->
            <section class="bg-white dark:bg-gray-800 shadow-sm rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <h2 class="text-xl font-semibold text-[#035a76] dark:text-[#4db3ff] mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
                Contact & Emergency
              </h2>
              <dl class="space-y-5 text-sm">
                <div>
                  <dt class="text-gray-500 dark:text-gray-400 font-medium">Phone Number</dt>
                  <dd class="mt-1 text-gray-900 dark:text-gray-100">
                    {{ employee.personal.phone || employee.contact?.phone || '—' }}
                  </dd>
                </div>
                <div>
                  <dt class="text-gray-500 dark:text-gray-400 font-medium">Email Address</dt>
                  <dd class="mt-1">{{ employee.personal.email || employee.contact?.email || '—' }}</dd>
                </div>
                <div>
                  <dt class="text-gray-500 dark:text-gray-400 font-medium">Residential Address</dt>
                  <dd class="mt-1 whitespace-pre-line">
                    {{ employee.personal.address || employee.contact?.residential_address || '—' }}
                  </dd>
                </div>

                <div v-if="employee.emergency_contact?.name" class="pt-4 border-t border-gray-100 dark:border-gray-700">
                  <dt class="text-gray-500 dark:text-gray-400 font-medium">Emergency Contact</dt>
                  <dd class="mt-1">
                    <p class="font-medium">{{ employee.emergency_contact.name }}</p>
                    <p class="text-gray-600 dark:text-gray-300">
                      {{ employee.emergency_contact.relationship }} • {{ employee.emergency_contact.phone }}
                      <span v-if="employee.emergency_contact.alt_phone"> / {{ employee.emergency_contact.alt_phone }}</span>
                    </p>
                    <p class="text-gray-500 dark:text-gray-400 text-sm mt-0.5 whitespace-pre-line">
                      {{ employee.emergency_contact.address || '' }}
                    </p>
                  </dd>
                </div>
              </dl>
            </section>

            <!-- Bank + Identification + Contract Summary (merged - full width) -->
            <section class="bg-white dark:bg-gray-800 shadow-sm rounded-xl p-6 border border-gray-200 dark:border-gray-700 lg:col-span-2">
              <h2 class="text-xl font-semibold text-[#035a76] dark:text-[#4db3ff] mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
                Banking, Identification & Contract
              </h2>
              <div class="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
                <!-- Bank Account -->
                <div>
                  <h3 class="text-base font-semibold mb-3 text-gray-800 dark:text-gray-200">Bank Account</h3>
                  <dl class="space-y-3 text-sm">
                    <div>
                      <dt class="text-gray-500 dark:text-gray-400">Bank Name</dt>
                      <dd class="font-medium">{{ employee.bank_account?.bank_name || '—' }}</dd>
                    </div>
                    <div>
                      <dt class="text-gray-500 dark:text-gray-400">Branch Name</dt>
                      <dd class="font-medium">{{ employee.bank_account?.branch_name || '—' }}</dd>
                    </div>                    
                    <div>
                      <dt class="text-gray-500 dark:text-gray-400">Account Name</dt>
                      <dd>{{ employee.bank_account?.account_name || '—' }}</dd>
                    </div>
                    <div>
                      <dt class="text-gray-500 dark:text-gray-400">Account Number</dt>
                      <dd class="font-mono">{{ employee.bank_account?.account_number || '—' }}</dd>
                    </div>
                  </dl>
                </div>

                <!-- Identification -->
                <div>
                  <h3 class="text-base font-semibold mb-3 text-gray-800 dark:text-gray-200">Identification</h3>
                  <dl class="space-y-3 text-sm">
                    <div>
                      <dt class="text-gray-500 dark:text-gray-400">Ghana Card</dt>
                      <dd class="font-medium">{{ employee.identifications?.ghana_card_number || '—' }}</dd>
                    </div>
                    <div>
                      <dt class="text-gray-500 dark:text-gray-400">Expiry</dt>
                      <dd>{{ formatDate(employee.identifications?.ghana_card_expiry) }}</dd>
                    </div>
                    <div>
                      <dt class="text-gray-500 dark:text-gray-400">SSNIT Number</dt>
                      <dd>{{ employee.identifications?.ssnit_number || '—' }}</dd>
                    </div>
                  </dl>
                </div>

                <!-- Current Contract (if available) -->
                <div v-if="employee.contract">
                  <h3 class="text-base font-semibold mb-3 text-gray-800 dark:text-gray-200">Current Contract</h3>
                  <dl class="space-y-3 text-sm">
                    <div>
                      <dt class="text-gray-500 dark:text-gray-400">Employee Code</dt>
                      <dd class="font-medium">{{ employee.contract?.employee_code || '—' }}</dd>
                    </div>
                    <div>
                      <dt class="text-gray-500 dark:text-gray-400">Status</dt>
                      <dd>
                        <span :class="getStatusClass(employee.contract?.status)" class="px-2.5 py-0.5 rounded-full text-xs font-medium">
                          {{ employee.contract?.status || 'No contract' }}
                        </span>
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>
            </section>

            <!-- Family Information (full width) -->
            <section class="lg:col-span-2 bg-white dark:bg-gray-800 shadow-sm rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <h2 class="text-xl font-semibold text-[#035a76] dark:text-[#4db3ff] mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
                Family Information
              </h2>

              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <!-- Spouse -->
                <div v-if="employee.spouse" class="space-y-4">
                  <h3 class="text-base font-semibold text-gray-800 dark:text-gray-200">Spouse</h3>
                  <div class="bg-gray-50 dark:bg-gray-700/40 p-4 rounded-lg">
                    <p class="font-medium">{{ employee.spouse.name || '—' }}</p>
                    <p class="text-gray-600 dark:text-gray-300 mt-1">{{ employee.spouse.phone || '—' }}</p>
                  </div>
                </div>

                <!-- Children -->
                <div v-if="employee.children?.length" class="space-y-4">
                  <h3 class="text-base font-semibold text-gray-800 dark:text-gray-200">Children</h3>
                  <div class="space-y-3">
                    <div v-for="(child, i) in employee.children" :key="child.id || i" class="bg-gray-50 dark:bg-gray-700/40 p-4 rounded-lg">
                      <p class="font-medium">{{ child.full_name || '—' }}</p>
                      <p class="text-sm text-gray-600 dark:text-gray-300">Age: {{ child.age || '—' }}</p>
                    </div>
                  </div>
                </div>

                <!-- Parents -->
                <div v-if="employee.parents?.length" class="space-y-4">
                  <h3 class="text-base font-semibold text-gray-800 dark:text-gray-200">Parents</h3>
                  <div v-for="parent in employee.parents" :key="parent.id" class="bg-gray-50 dark:bg-gray-700/40 p-4 rounded-lg">
                    <p class="font-medium capitalize">{{ parent.parent_type }} • {{ parent.name }}</p>
                    <p class="text-sm text-gray-600 dark:text-gray-300 mt-1">{{ parent.contact || '—' }}</p>
                    <p class="text-sm text-gray-500 dark:text-gray-400 mt-1 whitespace-pre-line">{{ parent.address || '' }}</p>
                  </div>
                </div>
              </div>
            </section>
          </div>

          <!-- Education & Work Experience (full width sections) -->
          <div class="mt-8 space-y-8">
            <!-- Education -->
            <section v-if="employee.education?.length" class="bg-white dark:bg-gray-800 shadow-sm rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <h2 class="text-xl font-semibold text-[#035a76] dark:text-[#4db3ff] mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
                Education Background
              </h2>
              <div class="space-y-6">
                <div v-for="edu in employee.education" :key="edu.id" class="border-l-4 border-secondary pl-5">
                  <p class="font-semibold text-gray-900 dark:text-gray-100">
                    {{ edu.certificate || '—' }}
                    <span v-if="edu.other_certificate">({{ edu.other_certificate }})</span>
                  </p>
                  <p class="text-gray-700 dark:text-gray-300">{{ edu.institution || '—' }}</p>
                  <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {{ edu.start_year || '—' }} — {{ edu.end_year || 'Present' }}
                  </p>
                </div>
              </div>
            </section>

            <!-- Work Experience -->
            <section v-if="employee.work_experience?.length" class="bg-white dark:bg-gray-800 shadow-sm rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <h2 class="text-xl font-semibold text-[#035a76] dark:text-[#4db3ff] mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
                Professional Experience
              </h2>
              <div class="space-y-6">
                <div v-for="work in employee.work_experience" :key="work.id" class="border-l-4 border-secondary pl-5">
                  <p class="font-semibold text-gray-900 dark:text-gray-100">
                    {{ work.position }} at {{ work.company_name || '—' }}
                  </p>
                  <p class="text-gray-700 dark:text-gray-300 text-sm">{{ work.company_address || '—' }}</p>
                  <p class="mt-2 text-sm">
                    <span class="font-medium">Period:</span> 
                    {{ formatDate(work.start_date) }} — {{ formatDate(work.end_date) || 'Present' }}
                  </p>
                  <p class="mt-1 text-sm text-gray-600 dark:text-gray-300">
                    <span class="font-medium">Reason for leaving:</span> {{ work.reason_for_leaving || '—' }}
                  </p>
                  <p v-if="work.contact_person_phone" class="mt-1 text-sm">
                    <span class="font-medium">Contact:</span> {{ work.contact_person_phone }}
                  </p>
                </div>
              </div>
            </section>
          </div>
        </template>

        <!-- No data state -->
        <div v-else class="text-center py-16 text-gray-500 dark:text-gray-400">
          No employee data available
        </div>
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import HrSidebar from '../../components/hr/HrSidebar.vue'
import HrTopbar from '../../components/hr/HrTopbar.vue'
import api from '@/services/api'
import { useRoute, useRouter } from 'vue-router'

const route = useRoute()
const router = useRouter()

const applicantId = route.params.id

const isSidebarOpen = ref(true)
const isDarkMode = ref(false)
const currentPageTitle = ref('Staff Details')

const employee = ref({})
const loading = ref(true)
const error = ref(null)

const toggleSidebar = () => { isSidebarOpen.value = !isSidebarOpen.value }

const toggleDarkMode = () => {
  isDarkMode.value = !isDarkMode.value
  if (isDarkMode.value) {
    document.documentElement.classList.add('dark')
    localStorage.theme = 'dark'
  } else {
    document.documentElement.classList.remove('dark')
    localStorage.theme = 'light'
  }
}

const formatDate = (dateString) => {
  if (!dateString) return '—'
  try {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  } catch {
    return '—'
  }
}

const getStatusClass = (status) => {
  const s = (status || '').toLowerCase()
  if (s === 'active') return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
  if (s === 'on leave') return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
  if (s === 'inactive') return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
  return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
}

const fetchEmployeeProfile = async () => {
  try {
    loading.value = true
    error.value = null
    const response = await api.get(`get_employee_profile.php?id=${applicantId}`)
    if (response.data?.success) {
      employee.value = response.data
    } else {
      error.value = 'Invalid response from server'
    }
  } catch (err) {
    console.error('Error fetching employee profile:', err)
    error.value = err.message || 'Network error'
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchEmployeeProfile()
})
</script>